/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author   Tomáš Burian    (burian@cadwork.cz)
 *
 */

import {EEmbededElementTypes, RenderDocument} from "js_renderer";
import Picker from "./picker";
import {vec2, vec4} from "gl-matrix";
import AbstractElement from "../../elements/abstract_element";
import PoiEntity from "../../elements/geometry_entities/poi_entity";

export default
class PoiPicker extends Picker {
    private _pickedObject: AbstractElement;
    private _pgc: { msPositionForRoundedWindowPoint: any; windowPoint: vec4.create };
    protected _supportedTypes: EEmbededElementTypes[];

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._pickedObject = null;

        this._pgc = {
            windowPoint: new vec4.create(),
            msPositionForRoundedWindowPoint: vec4.create()
        };

        this._supportedTypes = [
            EEmbededElementTypes.POIENTITY,
            EEmbededElementTypes.ANIMATIONENTITY,
            EEmbededElementTypes.MEASUREPOINTENTITY
        ];
    }

    public onMove(aX: number, aY: number, aButtonState: number) {
        if (!this._enabled || this.isPaused()) {
            return false;
        }

        let sceneElement = this.pickSceneElement(this._supportedTypes);
        let change = (this._pickedObject !== sceneElement);

        // TB send all elements to detect mouse moved from poi flag and poi must be hidden
        if (sceneElement) {
            if (this._pickedObject && sceneElement) {
                change = !this._pickedObject.compareContent(sceneElement);
            }

            if (this._callbacks.onMove !== undefined && change) {
                if (this._pickedObject) {
                    this._callbacks.onMove(this._pickedObject, false);
                }

                let screenPos = this.getPoiScreenPosition(sceneElement, this._sceneAPI.viewModeModule.getActiveView());
                this._callbacks.onMove(sceneElement, true, screenPos[0], screenPos[1]);
            }

            this._pickedObject = sceneElement;
        } else {
            if (this._callbacks.onMove !== undefined) {
                this._callbacks.onMove(this._pickedObject, false);
            }
            this._pickedObject = null;
        }
    }

    public onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean) {
        if (!this._enabled || aDraggedDuringClick || this.isPaused()) {
            return false;
        }

        let sceneElement = this.pickSceneElement(this._supportedTypes);

        // TB send all elements to detect mouse moved from poi flag and poi must be hidden
        if (sceneElement) {
            // transform from physial resolution to to css resolution
            let pagePos = this._sceneAPI.getCanvas().getPageMousePositionFromPhysPosition([x, y]);

            if (this._callbacks.onClick !== undefined) {
                this._callbacks.onClick(sceneElement, false, pagePos[0], pagePos[1]);
            }
        } else {
            if (this._callbacks.onClick !== undefined) {
                this._callbacks.onClick(null, false);
            }
        }
    }

    public onSingleTap(x: number, y: number) {
        if (!this._enabled || this.isPaused()) {
            return false;
        }

        let sceneElement = this.pickSceneElement(this._supportedTypes);

        // TB send only poi elements
        // poi can be shown / hiden only when its geometry is touched
        if (sceneElement) {
            // transform from physial resolution to to css resolution
            let pagePos = this._sceneAPI.getCanvas().getPageMousePositionFromPhysPosition([x, y]);

            if (this._callbacks.onSingleTap !== undefined) {
                this._callbacks.onSingleTap(sceneElement, true, pagePos[0], pagePos[1]);
            }
        }
    }

    /**
     * Recompute the screen position based on the current transformation in camera in current view.
     *
     * @param {PoiEntity} aEntity scene API element
     * @param {View} aView actual view for which the wscreen position is computed
     */
    public getPoiScreenPosition(aEntity: PoiEntity, aView: any): vec2 {
        // transform the 3D position of this annotation to viewport
        let windowPoint = aView.projectPointToWindow(aEntity.getPosition());


        // transform from physial resolution to to css resolution
        let pagePos = this._sceneAPI.getCanvas().getPageMousePositionFromPhysPosition([windowPoint[0], windowPoint[1]]);

        return vec2.fromValues(pagePos[0], pagePos[1]);
    }
}
